import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  RefreshCw,
  Package,
  Truck,
  MapPin,
  ChevronLeft,
  Check,
  AlertCircle,
  Image as ImageIcon,
} from 'lucide-react';
import { api } from '../services/api';
import {
  SHIPPING_CARRIERS,
  ITEM_CONDITION_LABELS,
  type ItemWithImages,
  type ShippingCarrierId,
  type ItemCondition,
} from '@findmystuff/shared';
import { ImageLightbox } from '../components/common/ImageLightbox';
import { DiscogsSellModal } from '../components/selling/DiscogsSellModal';
import { useSellingStore } from '../store/selling';

// Format date to relative format
const formatRelativeDate = (date: string | Date) => {
  const d = new Date(date);
  const now = new Date();
  const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((todayOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

interface OrderGroup {
  orderId: string | null;
  items: ItemWithImages[];
  soldAt: Date | string;
}

function groupItemsByOrder(items: ItemWithImages[]): OrderGroup[] {
  const orderMap = new Map<string, ItemWithImages[]>();
  const noOrderItems: ItemWithImages[] = [];

  for (const item of items) {
    const orderId = (item as any).discogsOrderId;
    if (orderId) {
      const existing = orderMap.get(orderId) || [];
      existing.push(item);
      orderMap.set(orderId, existing);
    } else {
      noOrderItems.push(item);
    }
  }

  const groups: OrderGroup[] = [];

  // Add order groups
  for (const [orderId, orderItems] of orderMap) {
    groups.push({
      orderId,
      items: orderItems,
      soldAt: orderItems[0]?.soldAt || orderItems[0]?.createdAt || new Date(),
    });
  }

  // Add individual items without orders
  for (const item of noOrderItems) {
    groups.push({
      orderId: null,
      items: [item],
      soldAt: item.soldAt || item.createdAt,
    });
  }

  // Sort by soldAt ascending (oldest first)
  groups.sort((a, b) => new Date(a.soldAt).getTime() - new Date(b.soldAt).getTime());

  return groups;
}

interface ShipFormState {
  trackingNumber: string;
  carrier: ShippingCarrierId;
  isSubmitting: boolean;
}

function OrderGroupCard({
  group,
  onItemClick,
}: {
  group: OrderGroup;
  onItemClick: (itemId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [shipForm, setShipForm] = useState<ShipFormState>({
    trackingNumber: '',
    carrier: 'canadapost',
    isSubmitting: false,
  });
  const [lightboxImage, setLightboxImage] = useState<{ url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleMarkShipped = async () => {
    if (shipForm.isSubmitting) return;
    setShipForm((prev) => ({ ...prev, isSubmitting: true }));
    setError(null);

    try {
      const firstItem = group.items[0]!;
      const now = new Date().toISOString();

      // 1. Update item (backend propagates to siblings)
      await api.items.update(firstItem.id, {
        shippedAt: new Date(now),
        trackingNumber: shipForm.trackingNumber || null,
        trackingCarrier: shipForm.carrier,
        shippingSource: 'manual',
      });

      // 2. If has Discogs order, also update Discogs
      if (group.orderId) {
        try {
          const carrierName = SHIPPING_CARRIERS.find((c) => c.id === shipForm.carrier)?.name;
          await api.discogs.updateOrderShipping(
            group.orderId,
            shipForm.trackingNumber || undefined,
            carrierName
          );
        } catch {
          // Don't fail the whole operation if Discogs update fails
        }
      }

      // 3. Invalidate queries to refresh list
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as shipped');
      setShipForm((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  if (success) {
    return (
      <div className="card p-4 bg-green-50 border border-green-200">
        <div className="flex items-center gap-2 text-green-700">
          <Check className="w-5 h-5" />
          <span className="font-medium">
            {group.orderId ? `Order ${group.orderId}` : group.items[0]?.title} marked as shipped!
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Order Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-gray-500" />
          <div>
            <span className="font-medium text-gray-900">
              {group.orderId ? `Order #${group.orderId}` : 'Individual Item'}
            </span>
            <span className="text-sm text-gray-500 ml-2">
              Sold {formatRelativeDate(group.soldAt)}
            </span>
          </div>
        </div>
        <span className="text-sm text-gray-500">
          {group.items.length} item{group.items.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Items List */}
      <div className="divide-y divide-gray-50">
        {group.items.map((item) => (
          <div key={item.id} className="p-4 flex items-start gap-3">
            {/* Thumbnail */}
            <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              {item.images?.[0] ? (
                <img
                  src={item.images[0].thumbnailUrl || item.images[0].url}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <span className="text-xl text-gray-300">📦</span>
              )}
            </div>

            {/* Item Details */}
            <div className="flex-1 min-w-0">
              <button
                onClick={() => onItemClick(item.id)}
                className="font-medium text-gray-900 hover:text-primary-600 truncate block text-left w-full"
              >
                {item.title}
              </button>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mt-0.5">
                {(item as any).discogsMediaCondition && (
                  <span>Media: {(item as any).discogsMediaCondition}</span>
                )}
                {(item as any).discogsSleeveCondition && (
                  <span>Sleeve: {(item as any).discogsSleeveCondition}</span>
                )}
                {!(item as any).discogsMediaCondition && item.condition && (
                  <span>{ITEM_CONDITION_LABELS[item.condition as ItemCondition] || item.condition}</span>
                )}
                {(item as any).discogsPrice > 0 && (
                  <span className="font-medium text-emerald-600">
                    ${((item as any).discogsPrice as number).toFixed(2)}
                  </span>
                )}
              </div>

              {/* Location Info */}
              {item.location && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <MapPin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-amber-700">
                    {item.location}
                    {(item as any).locationPosition && (
                      <span className="text-amber-500 ml-1">
                        (#{(item as any).locationPosition})
                      </span>
                    )}
                  </span>
                  {item.locationImage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxImage({ url: item.locationImage! });
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                    >
                      <ImageIcon className="w-3 h-3" />
                      View
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Ship Form */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        {error && (
          <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Tracking Number</label>
            <input
              type="text"
              placeholder="Optional"
              value={shipForm.trackingNumber}
              onChange={(e) => setShipForm((prev) => ({ ...prev, trackingNumber: e.target.value }))}
              className="input text-sm w-full"
              disabled={shipForm.isSubmitting}
            />
          </div>
          <div className="sm:w-40">
            <label className="block text-xs font-medium text-gray-600 mb-1">Carrier</label>
            <select
              value={shipForm.carrier}
              onChange={(e) => setShipForm((prev) => ({ ...prev, carrier: e.target.value as ShippingCarrierId }))}
              className="input text-sm w-full"
              disabled={shipForm.isSubmitting}
            >
              {SHIPPING_CARRIERS.map((carrier) => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleMarkShipped}
            disabled={shipForm.isSubmitting}
            className="btn-primary text-sm py-2 px-4 flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {shipForm.isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Truck className="w-4 h-4" />
            )}
            Mark Shipped
          </button>
        </div>
      </div>

      {/* Location Image Lightbox */}
      {lightboxImage && (
        <ImageLightbox
          images={[lightboxImage]}
          onClose={() => setLightboxImage(null)}
          title="Location"
        />
      )}
    </div>
  );
}

export function NeedsShippingPage() {
  const queryClient = useQueryClient();
  const { openSellModal, sellModalItemId, closeSellModal } = useSellingStore();
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch all items needing shipping
  const { data: itemsData, isLoading } = useQuery({
    queryKey: ['items', { needsShipping: true }],
    queryFn: () =>
      api.items.list({
        needsShipping: true,
        pageSize: 100,
        sortBy: 'soldAt',
        sortOrder: 'asc',
      }),
  });

  const items = (itemsData?.items || []) as ItemWithImages[];
  const totalCount = itemsData?.meta?.totalCount || 0;
  const orderGroups = groupItemsByOrder(items);

  // Count unique orders
  const orderCount = orderGroups.length;

  // Sync sales mutation
  const syncSalesMutation = useMutation({
    mutationFn: () => api.discogs.syncSales(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setSyncMessage({
        type: 'success',
        text: data.synced > 0
          ? `Synced ${data.synced} sold item${data.synced !== 1 ? 's' : ''} from ${data.total} orders`
          : `No new sales found (checked ${data.total} orders)`,
      });
      setTimeout(() => setSyncMessage(null), 5000);
    },
    onError: () => {
      setSyncMessage({ type: 'error', text: 'Failed to sync sales from Discogs' });
      setTimeout(() => setSyncMessage(null), 5000);
    },
  });

  // Get selected item for modal
  const selectedModalItem = sellModalItemId
    ? items.find((i) => i.id === sellModalItemId)
    : null;

  // Fetch item if not in list (e.g. from a different page of results)
  const { data: fetchedModalItem } = useQuery({
    queryKey: ['item', sellModalItemId],
    queryFn: () => api.items.get(sellModalItemId!),
    enabled: !!sellModalItemId && !selectedModalItem,
    staleTime: 30000,
  });

  const modalItem = selectedModalItem || fetchedModalItem;

  return (
    <div className="space-y-6">
      {/* Sync message */}
      {syncMessage && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 ${
            syncMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {syncMessage.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{syncMessage.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/selling"
            className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="w-6 h-6 text-amber-500" />
              Needs Shipping
            </h1>
            {!isLoading && (
              <p className="text-gray-600 text-sm mt-0.5">
                {totalCount === 0
                  ? 'All caught up! No items to ship.'
                  : `${orderCount} order${orderCount !== 1 ? 's' : ''}, ${totalCount} item${totalCount !== 1 ? 's' : ''} to ship`}
              </p>
            )}
          </div>
        </div>

        <button
          onClick={() => syncSalesMutation.mutate()}
          disabled={syncSalesMutation.isPending}
          className="flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-4 py-2 rounded-lg transition-colors"
        >
          {syncSalesMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Sync Sales
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="card p-8 text-center text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      ) : orderGroups.length === 0 ? (
        <div className="card p-12 text-center">
          <Check className="w-12 h-12 mx-auto text-green-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">All shipped!</h3>
          <p className="text-gray-500 mb-4">No items waiting to be shipped.</p>
          <Link to="/selling" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            Back to Selling
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orderGroups.map((group) => (
            <OrderGroupCard
              key={group.orderId || group.items[0]?.id || 'unknown'}
              group={group}
              onItemClick={openSellModal}
            />
          ))}
        </div>
      )}

      {/* Discogs Sell Modal */}
      {sellModalItemId && modalItem && (
        <DiscogsSellModal
          item={modalItem}
          isOpen={!!sellModalItemId}
          onClose={closeSellModal}
        />
      )}
    </div>
  );
}
